/**
 * Draggable IK targets for the four limbs (ANIMATE mode, when IK is enabled).
 *
 * A handle is an editor-only sphere sitting where the hand or foot should go. Dragging it solves
 * the chain on the live bones and immediately writes the resulting canonical rotations into the
 * live pose (`ikApply.ts`), so nothing about the handle itself is ever serialized — the clip that
 * comes out is indistinguishable from one posed by hand.
 *
 * Inactive handles are glued to their end bone every frame. Without that they would sit wherever
 * they were last dropped while the playhead scrubs the limb somewhere else, and the next grab
 * would snap the limb violently back to a stale target.
 */

import {
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three';
import type { Camera } from 'three';

import { IK_CHAINS } from '../../shared';
import type { IkChain, IkChainName, Vec3Tuple } from '../../shared';
import type { EditorContext } from '../context';
import { applyIkChain, restingTargetForChain } from './ikApply';
import type { GizmoClaim } from './gizmos';

const HANDLE_RADIUS = 0.045;
const POLE_RADIUS = 0.028;
const PICK_RADIUS = 0.075;

/*
 * IK colours are a FUNCTION code (target vs pole), not an accent, so they stay distinct from the
 * orange selection and the blue UI state — but they are pulled onto the palette's green/violet
 * (`design/ui-design.md` §6, step 10).
 */
const COLOR_TARGET = new Color(0x6aa84f);
const COLOR_TARGET_ACTIVE = new Color(0x9ed17c);
const COLOR_POLE = new Color(0xa070c0);

interface Handle {
  chain: IkChain;
  /** The object the gizmo attaches to. Its position IS the target position. */
  target: Object3D;
  dot: Mesh;
  pick: Mesh;
  material: MeshBasicMaterial;
  pole: Object3D;
  poleDot: Mesh;
  polePick: Mesh;
}

export interface IkHandleLayer {
  /** Reposition handles from state. Skips whichever handle is mid-drag. */
  update(draggingKey: string | null): void;
  /** Gizmo claim for the active handle, or null when IK is off / nothing selected. */
  claim(): GizmoClaim | null;
  /** Pointer pick; selects the chain and activates it. Returns true when a handle was hit. */
  pick(ndc: Vector2, camera: Camera): boolean;
  dispose(): void;
}

export function createIkHandleLayer(overlayGroup: Group, ctx: EditorContext): IkHandleLayer {
  const group = new Group();
  group.name = 'asx-ik';
  group.userData.editorOnly = true;
  overlayGroup.add(group);

  const dotGeometry = new SphereGeometry(HANDLE_RADIUS, 12, 10);
  const poleGeometry = new SphereGeometry(POLE_RADIUS, 8, 6);
  const pickGeometry = new SphereGeometry(PICK_RADIUS, 6, 5);
  const pickMaterial = new MeshBasicMaterial({ visible: false, depthTest: false });
  const poleMaterial = new MeshBasicMaterial({ color: COLOR_POLE, depthTest: false });

  const handles = new Map<IkChainName, Handle>();
  const pickables: Mesh[] = [];
  const pickOwner = new Map<Object3D, { chain: IkChainName; kind: 'target' | 'pole' }>();

  for (const chain of IK_CHAINS) {
    const material = new MeshBasicMaterial({ color: COLOR_TARGET.clone(), depthTest: false });
    const dot = new Mesh(dotGeometry, material);
    dot.renderOrder = 1000;
    dot.frustumCulled = false;

    const target = new Object3D();
    target.name = `asx-ik-${chain.name}`;
    target.add(dot);

    const pick = new Mesh(pickGeometry, pickMaterial);
    pick.frustumCulled = false;
    target.add(pick);

    const pole = new Object3D();
    pole.name = `asx-ikpole-${chain.name}`;
    const poleDot = new Mesh(poleGeometry, poleMaterial);
    poleDot.renderOrder = 1000;
    poleDot.frustumCulled = false;
    pole.add(poleDot);
    const polePick = new Mesh(pickGeometry, pickMaterial);
    polePick.frustumCulled = false;
    pole.add(polePick);

    group.add(target, pole);
    pickables.push(pick, polePick);
    pickOwner.set(pick, { chain: chain.name, kind: 'target' });
    pickOwner.set(polePick, { chain: chain.name, kind: 'pole' });

    const handle: Handle = { chain, target, dot, pick, material, pole, poleDot, polePick };
    handles.set(chain.name, handle);
  }

  const chainByName = new Map<IkChainName, IkChain>(IK_CHAINS.map((c) => [c.name, c]));

  /** Which chain the gizmo is currently offered for, and whether it grabs target or pole. */
  let activeChain: IkChainName | null = null;
  let activeKind: 'target' | 'pole' = 'target';

  function enabled(): boolean {
    const state = ctx.store.getState();
    return state.mode === 'animate' && state.ik.enabled && ctx.session.rig !== null;
  }

  function setVisible(visible: boolean): void {
    group.visible = visible;
  }

  // --- per-frame placement ------------------------------------------------

  function update(draggingKey: string | null): void {
    const on = enabled();
    setVisible(on);
    if (!on) {
      activeChain = null;
      return;
    }

    const state = ctx.store.getState();
    const rig = ctx.session.rig;
    if (!rig) return;

    for (const handle of handles.values()) {
      const name = handle.chain.name;
      const stored = state.ik.targets[name];
      const isActive = Boolean(stored?.active);
      const dragging = draggingKey === ikClaimKey(name, 'target');

      if (!dragging) {
        // Active chains hold the target the user placed. Inactive ones ride along with the pose.
        const position: Vec3Tuple =
          isActive && stored ? stored.position : restingTargetForChain(rig, handle.chain);
        handle.target.position.set(position[0], position[1], position[2]);
        handle.target.updateMatrixWorld(true);
      }

      handle.material.color.copy(isActive ? COLOR_TARGET_ACTIVE : COLOR_TARGET);
      handle.dot.scale.setScalar(isActive ? 1.25 : 1);

      const pole = stored?.pole;
      handle.pole.visible = Boolean(pole) && isActive;
      if (pole && draggingKey !== ikClaimKey(name, 'pole')) {
        handle.pole.position.set(pole[0], pole[1], pole[2]);
        handle.pole.updateMatrixWorld(true);
      }
    }
  }

  // --- solving ------------------------------------------------------------

  const _target = new Vector3();
  const _pole = new Vector3();

  function solveActive(kind: 'target' | 'pole'): void {
    const rig = ctx.session.rig;
    if (!rig || !activeChain) return;
    const chain = chainByName.get(activeChain);
    const handle = handles.get(activeChain);
    if (!chain || !handle) return;

    handle.target.getWorldPosition(_target);
    const stored = ctx.store.getState().ik.targets[activeChain];
    const hasPole = Boolean(stored?.pole) || kind === 'pole';
    if (hasPole) handle.pole.getWorldPosition(_pole);

    applyIkChain({
      rig,
      chain,
      targetWorld: _target,
      poleWorld: hasPole ? _pole : undefined,
      setLivePoseBone: (bone, rotation, options) =>
        ctx.store.actions.setLivePoseBone(bone, rotation, options),
    });

    ctx.store.actions.setIkTarget(
      activeChain,
      [_target.x, _target.y, _target.z],
      hasPole ? [_pole.x, _pole.y, _pole.z] : stored?.pole,
    );
  }

  // --- gizmo claim --------------------------------------------------------

  function ikClaimKey(chain: IkChainName, kind: 'target' | 'pole'): string {
    return `ik:${chain}:${kind}`;
  }

  function claim(): GizmoClaim | null {
    if (!enabled() || !activeChain) return null;
    const handle = handles.get(activeChain);
    if (!handle) return null;
    const chain = activeChain;
    const kind = activeKind;
    return {
      key: ikClaimKey(chain, kind),
      object: kind === 'pole' ? handle.pole : handle.target,
      mode: 'translate',
      space: 'world',
      onChange: () => solveActive(kind),
      onDragStart: () => ctx.store.actions.beginIkDrag(chain),
      onDragEnd: () => ctx.store.actions.endIkDrag(),
    };
  }

  // --- picking ------------------------------------------------------------

  const raycaster = new Raycaster();

  function pick(ndc: Vector2, camera: Camera): boolean {
    if (!enabled()) return false;
    const visible = pickables.filter((p) => {
      let o: Object3D | null = p;
      while (o) {
        if (!o.visible) return false;
        o = o.parent;
      }
      return true;
    });
    if (visible.length === 0) return false;

    raycaster.setFromCamera(ndc, camera);
    const hit = raycaster.intersectObjects(visible, false)[0];
    if (!hit) return false;

    const owner = pickOwner.get(hit.object);
    const rig = ctx.session.rig;
    if (!owner || !rig) return false;

    const chain = chainByName.get(owner.chain);
    const handle = handles.get(owner.chain);
    if (!chain || !handle) return false;

    activeChain = owner.chain;
    activeKind = owner.kind;

    // First grab of a chain: seed the target where the limb already is, so activating IK never
    // yanks the pose. `activateIk` is a no-op until a target exists, hence the order.
    const stored = ctx.store.getState().ik.targets[owner.chain];
    if (!stored) {
      const rest = restingTargetForChain(rig, chain);
      ctx.store.actions.setIkTarget(owner.chain, rest);
    }
    ctx.store.actions.activateIk(owner.chain, true);
    ctx.store.actions.selectBone(chain.end);
    return true;
  }

  function dispose(): void {
    overlayGroup.remove(group);
    for (const handle of handles.values()) handle.material.dispose();
    handles.clear();
    pickables.length = 0;
    pickOwner.clear();
    dotGeometry.dispose();
    poleGeometry.dispose();
    pickGeometry.dispose();
    pickMaterial.dispose();
    poleMaterial.dispose();
  }

  return { update, claim, pick, dispose };
}
